/**
 * LaneCult cart gift engine
 * ------------------------------------------------------------------------
 * Keeps automatically added gift lines in sync with the cart:
 *   - LaneCult+ membership gift: one gift while the membership is in the cart.
 *   - Only ONE free gift per order: the membership gift, else the highest
 *     spend milestone reached.
 *   - Lines of one variant that differ only in hidden properties are merged.
 *     Lines Shopify split for a discount (same properties) are left alone;
 *     the cart shows them as one row (see LcCartGroups below).
 *
 * Every gift line carries the line item property `_lc_gift` = reward key
 * ("member", "crm1", "crm2", "crm3"). Underscore properties are hidden at
 * checkout. Products a customer adds themselves never carry it, so they are
 * never treated as gifts. Lines with the legacy `_lc_free_gift` property are
 * adopted as the "crm2" gift.
 *
 * Loop safety
 *   - The engine is idempotent: it reads /cart.js, computes the desired state
 *     and only mutates when the cart differs from it.
 *   - Gift lines are excluded from the milestone total, so adding or removing a
 *     gift can never unlock or lock another reward.
 *   - Its requests send `X-LC-Gift: 1`; the cart queue in cart-drawer.liquid
 *     does not re-trigger the engine (or open the drawer) for them.
 *   - Single flight + debounce, and a circuit breaker caps mutating passes.
 *   - Variants that fail to add, and rewards whose gift is not discounted to 0,
 *     are remembered for the session so they are not retried in a loop.
 *
 * Config: <script type="application/json" id="LcCartGiftsConfig"> in
 * snippets/cart-drawer.liquid (built from Theme settings).
 */
(() => {
  if (window.LcCartGifts) return;

  const configEl = document.getElementById('LcCartGiftsConfig');
  if (!configEl) return;

  let config;
  try {
    config = JSON.parse(configEl.textContent);
  } catch (error) {
    console.warn('[LcCartGifts] Invalid config', error);
    return;
  }

  const GIFT_PROP = '_lc_gift';
  const LEGACY_PROP = '_lc_free_gift';
  const LEGACY_KEY = 'crm2';
  const MEMBER_KEY = 'member';
  const ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  // Circuit breaker: max gift-mutating passes in a row WITHOUT a customer cart
  // change in between. Customer changes reset it; only a runaway loop trips it.
  const MAX_MUTATING_PASSES = 4;
  const PASS_WINDOW_MS = 15000;
  const BLOCK_TTL_MS = 10 * 60 * 1000;
  const QUEUE_WAIT_MS = 150;

  /* ---------------------------------------------------------------------- */
  /* Storage helpers                                                         */
  /* ---------------------------------------------------------------------- */
  const makeStore = (getStorage) => ({
    get(key) {
      try {
        const raw = getStorage().getItem(key);
        return raw == null ? null : JSON.parse(raw);
      } catch (error) {
        return null;
      }
    },
    set(key, value) {
      try {
        getStorage().setItem(key, JSON.stringify(value));
      } catch (error) {
        /* storage full or blocked: the engine still works, just without memory */
      }
    },
    remove(key) {
      try {
        getStorage().removeItem(key);
      } catch (error) {
        /* noop */
      }
    },
  });

  // Picks and loop cursors survive page loads; failures/blocks are per session.
  const local = makeStore(() => window.localStorage);
  const session = makeStore(() => window.sessionStorage);

  const pickKey = (rewardKey) => `lcGift:pick:${rewardKey}`;
  const cursorKey = (rewardKey) => `lcGift:cursor:${rewardKey}`;
  const failedKey = (rewardKey) => `lcGift:failed:${rewardKey}`;
  const blockedKey = (rewardKey) => `lcGift:blocked:${rewardKey}`;

  /* ---------------------------------------------------------------------- */
  /* Rewards                                                                 */
  /* ---------------------------------------------------------------------- */
  const toPool = (pool) =>
    (Array.isArray(pool) ? pool : [])
      .map((gift) => ({ v: Number(gift.v), p: Number(gift.p), t: gift.t || '' }))
      .filter((gift) => gift.v > 0);

  const rewards = [];
  const membership = config.membership || {};
  const memberProductId = Number(membership.productId) || 0;

  if (membership.enabled && memberProductId) {
    rewards.push({
      key: MEMBER_KEY,
      kind: 'member',
      rotation: membership.rotation === 'loop' ? 'loop' : 'random',
      pool: toPool(membership.pool),
    });
  }

  (config.tiers || []).forEach((tier) => {
    if (!tier || !tier.key) return;
    rewards.push({
      key: String(tier.key),
      kind: 'tier',
      threshold: Number(tier.threshold) || 0,
      rotation: tier.rotation === 'random' ? 'random' : 'loop',
      pool: toPool(tier.pool),
    });
  });

  const rewardByKey = new Map(rewards.map((reward) => [reward.key, reward]));

  // "Only keep gifts that are free" is enforced on the published theme only.
  // On preview / unpublished themes and in the editor, charged gifts are kept
  // (and flagged in the drawer) so gift add/remove can be tested before the
  // automatic discounts exist.
  const themeRole = window.Shopify && window.Shopify.theme && window.Shopify.theme.role;
  const isLiveTheme = !themeRole || themeRole === 'main';
  const requireFree = config.requireFree !== false && isLiveTheme;

  /* ---------------------------------------------------------------------- */
  /* Cart model                                                              */
  /* ---------------------------------------------------------------------- */
  const giftKeyOf = (item) => {
    const properties = item.properties || {};
    if (properties[GIFT_PROP]) return String(properties[GIFT_PROP]);
    if (properties[LEGACY_PROP]) return LEGACY_KEY;
    return null;
  };

  // Keep in sync with snippets/lc-cart-qualifying-total.liquid
  const qualifyingTotal = (cart) =>
    cart.items.reduce((sum, item) => (giftKeyOf(item) ? sum : sum + item.final_line_price), 0);

  const hasMembership = (cart) =>
    cart.items.some((item) => !giftKeyOf(item) && Number(item.product_id) === memberProductId);

  const isBlocked = (rewardKey) => {
    const blockedAt = session.get(blockedKey(rewardKey));
    return Boolean(blockedAt) && Date.now() - blockedAt < BLOCK_TTL_MS;
  };

  const block = (rewardKey) => {
    session.set(blockedKey(rewardKey), Date.now());
    local.remove(pickKey(rewardKey));
  };

  const failedVariants = (rewardKey) => session.get(failedKey(rewardKey)) || [];

  const markFailed = (rewardKey, variantId) => {
    const failed = failedVariants(rewardKey);
    if (!failed.includes(variantId)) session.set(failedKey(rewardKey), failed.concat(variantId));
    local.remove(pickKey(rewardKey));
  };

  const isRewardActive = (reward, cart, total) => {
    if (!reward.pool.length || isBlocked(reward.key)) return false;
    return reward.kind === 'member' ? hasMembership(cart) : total >= reward.threshold;
  };

  /**
   * Picks the gift for a reward that just became active.
   * - A remembered pick (same activation) is reused, so the gift stays stable.
   * - "loop" walks the pool in order: A → B → C → A …
   * - "random" picks any eligible gift.
   */
  const chooseGift = (reward) => {
    const failed = failedVariants(reward.key);
    const candidates = reward.pool.filter((gift) => !failed.includes(gift.v));
    if (!candidates.length) return null;

    const remembered = local.get(pickKey(reward.key));
    const rememberedGift = candidates.find((gift) => gift.v === remembered);
    if (rememberedGift) return rememberedGift;

    let gift = null;
    if (reward.rotation === 'loop') {
      const lastIndex = reward.pool.findIndex((item) => item.v === local.get(cursorKey(reward.key)));
      for (let step = 1; step <= reward.pool.length; step += 1) {
        const next = reward.pool[(lastIndex + step) % reward.pool.length];
        if (!failed.includes(next.v)) {
          gift = next;
          break;
        }
      }
      if (gift) local.set(cursorKey(reward.key), gift.v);
    } else {
      gift = candidates[Math.floor(Math.random() * candidates.length)];
    }

    if (gift) local.set(pickKey(reward.key), gift.v);
    return gift;
  };

  /**
   * Computes the changes needed to bring the cart to the desired state.
   * Returns { updates: {lineKey: qty}, adds: [{rewardKey, gift}] }.
   */
  const plan = (cart) => {
    const total = qualifyingTotal(cart);
    let active = rewards.filter((reward) => isRewardActive(reward, cart, total));

    // One free gift per order: the LaneCult+ membership gift wins; without the
    // membership, only the highest spend milestone reached gives a gift.
    if (active.length > 1) {
      const withGifts = active.filter((reward) => reward.pool.length > 0);
      const winner =
        withGifts.find((reward) => reward.kind === 'member') ||
        withGifts.filter((reward) => reward.kind === 'tier').sort((x, y) => y.threshold - x.threshold)[0];
      active = winner ? [winner] : [];
    }
    const activeKeys = new Set(active.map((reward) => reward.key));

    const updates = {};
    const kept = new Set();

    // One line per product: the same variant (and selling plan) added with
    // different line properties is merged into the first line. Lines with the
    // SAME properties were split by Shopify for a discount (e.g. one unit used
    // as the BXGY "buys" item); Shopify re-splits them after any merge, so
    // merging those would loop. They are shown as one row instead.
    const sameProduct = new Map();
    cart.items.forEach((item) => {
      if (giftKeyOf(item)) return;
      const sellingPlan = item.selling_plan_allocation && item.selling_plan_allocation.selling_plan;
      const id = `${item.variant_id}:${sellingPlan ? sellingPlan.id : 0}`;
      if (!sameProduct.has(id)) sameProduct.set(id, []);
      sameProduct.get(id).push(item);
    });
    sameProduct.forEach((lines) => {
      if (lines.length < 2) return;
      const propertySets = new Set(lines.map((line) => JSON.stringify(line.properties || {})));
      if (propertySets.size < 2) return;
      updates[lines[0].key] = lines.reduce((sum, line) => sum + line.quantity, 0);
      lines.slice(1).forEach((line) => {
        updates[line.key] = 0;
      });
    });

    cart.items.forEach((item) => {
      const rewardKey = giftKeyOf(item);
      if (!rewardKey) return;

      const reward = rewardByKey.get(rewardKey);
      const variantId = Number(item.variant_id);
      const isValid =
        reward &&
        activeKeys.has(rewardKey) &&
        !kept.has(rewardKey) &&
        reward.pool.some((gift) => gift.v === variantId) &&
        !failedVariants(rewardKey).includes(variantId);

      if (!isValid) {
        updates[item.key] = 0;
        return;
      }

      // A gift that is still charged means no discount covers it: never let a
      // customer pay for a line they cannot remove.
      if (requireFree && item.final_line_price > 0) {
        updates[item.key] = 0;
        block(rewardKey);
        warnNotFree(rewardKey, item);
        return;
      }

      kept.add(rewardKey);
      local.set(pickKey(rewardKey), variantId);
      if (item.quantity !== 1) updates[item.key] = 1;
    });

    const adds = [];
    activeKeys.forEach((rewardKey) => {
      if (kept.has(rewardKey) || isBlocked(rewardKey)) return;
      const gift = chooseGift(rewardByKey.get(rewardKey));
      if (gift) adds.push({ rewardKey, gift });
    });

    // Forget the pick of inactive rewards so the next activation picks again.
    rewards.forEach((reward) => {
      if (!activeKeys.has(reward.key)) local.remove(pickKey(reward.key));
    });

    return { updates, adds };
  };

  /* ---------------------------------------------------------------------- */
  /* Network                                                                 */
  /* ---------------------------------------------------------------------- */
  const getCart = async () => {
    const response = await fetch(`${ROOT}cart.js`, {
      headers: { Accept: 'application/json' },
      cache: 'no-store',
    });
    if (!response.ok) throw new Error(`cart.js ${response.status}`);
    return response.json();
  };

  const postCart = async (path, body) => {
    const response = await fetch(`${ROOT}${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-LC-Gift': '1' },
      body: JSON.stringify(body),
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      const error = new Error(data.description || data.message || `${path} ${response.status}`);
      error.status = response.status;
      throw error;
    }
    return data;
  };

  const addGift = async (rewardKey, firstGift) => {
    const reward = rewardByKey.get(rewardKey);
    let gift = firstGift;

    for (let attempt = 0; gift && attempt < 3; attempt += 1) {
      try {
        await postCart('cart/add.js', {
          items: [{ id: gift.v, quantity: 1, properties: { [GIFT_PROP]: rewardKey } }],
        });
        return true;
      } catch (error) {
        // 422 = sold out / not purchasable: skip this variant and try the next one.
        if (error.status !== 422) throw error;
        markFailed(rewardKey, gift.v);
        gift = chooseGift(reward);
      }
    }
    return false;
  };

  const warned = new Set();
  function warnNotFree(rewardKey, item) {
    if (warned.has(rewardKey)) return;
    warned.add(rewardKey);
    console.warn(
      `[LcCartGifts] Removed gift "${item.product_title}" (${rewardKey}): it is not discounted to 0. ` +
        'Create the matching automatic discount, or turn off "Only keep gifts that are free" in Theme settings.'
    );
  }

  /* ---------------------------------------------------------------------- */
  /* Sync loop                                                               */
  /* ---------------------------------------------------------------------- */
  let running = false;
  let rerunRequested = false;
  let timer = null;
  let passTimestamps = [];

  const schedule = (delay = 250) => {
    window.clearTimeout(timer);
    timer = window.setTimeout(sync, delay);
  };

  const queueIsBusy = () =>
    Boolean(window.lcCartQueue && typeof window.lcCartQueue.pending === 'function' && window.lcCartQueue.pending() > 0);

  const allowMutation = () => {
    const now = Date.now();
    passTimestamps = passTimestamps.filter((time) => now - time < PASS_WINDOW_MS);
    if (passTimestamps.length >= MAX_MUTATING_PASSES) {
      console.warn('[LcCartGifts] Paused: too many gift updates in a short time.');
      return false;
    }
    passTimestamps.push(now);
    return true;
  };

  async function sync() {
    // Runs even with no rewards configured, so leftover gift lines get cleaned up.
    if (running) {
      rerunRequested = true;
      return;
    }
    if (queueIsBusy()) {
      schedule(QUEUE_WAIT_MS);
      return;
    }

    running = true;
    try {
      const cart = await getCart();
      const { updates, adds } = plan(cart);
      const hasUpdates = Object.keys(updates).length > 0;
      if (!hasUpdates && !adds.length) return;
      if (!allowMutation()) return;

      if (hasUpdates) await postCart('cart/update.js', { updates });

      let added = false;
      for (const { rewardKey, gift } of adds) {
        if (await addGift(rewardKey, gift)) added = true;
      }

      // Verify discounts made the new gifts free (plan() removes them otherwise).
      if (added && requireFree) {
        const fresh = await getCart();
        const verified = plan(fresh);
        // Only gift lines are removed here; a merge needs its paired update.
        const giftLineKeys = new Set(fresh.items.filter((item) => giftKeyOf(item)).map((item) => item.key));
        const removals = Object.fromEntries(
          Object.entries(verified.updates).filter(([key, qty]) => qty === 0 && giftLineKeys.has(key))
        );
        if (Object.keys(removals).length) await postCart('cart/update.js', { updates: removals });
      }

      document.dispatchEvent(new CustomEvent('lc:gifts:updated'));
    } catch (error) {
      console.warn('[LcCartGifts]', error);
    } finally {
      running = false;
      if (rerunRequested) {
        rerunRequested = false;
        schedule(250);
      }
    }
  }

  /* ---------------------------------------------------------------------- */
  /* Triggers                                                                */
  /* ---------------------------------------------------------------------- */
  // Fired by the cart queue in cart-drawer.liquid after customer cart changes.
  document.addEventListener('lc:cart:mutated', () => {
    passTimestamps = [];
    schedule(0);
  });

  // Fallback for code paths that publish cart updates without the queue.
  if (typeof window.subscribe === 'function' && window.PUB_SUB_EVENTS && window.PUB_SUB_EVENTS.cartUpdate) {
    window.subscribe(window.PUB_SUB_EVENTS.cartUpdate, () => schedule(200));
  }

  // Back/forward cache restores a stale page: re-check.
  window.addEventListener('pageshow', (event) => {
    if (event.persisted) schedule(300);
  });

  // Initial check (page refresh, cart changed in another tab, etc.).
  schedule(600);

  window.LcCartGifts = {
    sync: () => schedule(0),
    isLiveTheme,
    rewards,
    giftKeyOf,
    qualifyingTotal,
    // Clears session memory (failed variants / not-free blocks) for testing.
    reset() {
      rewards.forEach((reward) => {
        session.remove(failedKey(reward.key));
        session.remove(blockedKey(reward.key));
        local.remove(pickKey(reward.key));
      });
      warned.clear();
      passTimestamps = [];
      schedule(0);
    },
  };
})();

/**
 * LaneCult cart progress bar: instant + smooth
 * ------------------------------------------------------------------------
 * Instant: when the customer presses + / − / Remove (or types a quantity),
 *   the bar, message and milestone dots update immediately from the unit
 *   prices already in the drawer (data-lc-unit), before Shopify answers.
 * Smooth: whenever the drawer HTML is re-rendered (Dawn's cart update, the
 *   cart queue refresh, gift updates), the new bar starts from the width that
 *   is on screen and animates to its new value instead of jumping.
 * Markup: snippets/cart-drawer-tracker.liquid, snippets/lc-cart-drawer-item.liquid
 */
(() => {
  if (window.LcCartProgress) return;

  const drawer = document.querySelector('cart-drawer');
  if (!drawer) return;

  const BAR_EASE = 'width 0.45s cubic-bezier(0.22, 1, 0.36, 1)';
  const DOT_EASE = 'background-color 0.3s ease, box-shadow 0.3s ease, color 0.3s ease';
  const reduceMotion = Boolean(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  const themeRole = window.Shopify && window.Shopify.theme && window.Shopify.theme.role;
  const isLiveTheme = !themeRole || themeRole === 'main';

  let shownRest = null; // width (%) of the grey "not reached" part currently on screen
  const pendingRemovals = new Set(); // lines removed on screen but not re-rendered yet

  const clamp = (value) => Math.max(0, Math.min(100, value));
  const round1 = (value) => Math.round(value * 10) / 10;

  /* Money formatting, same rules as Shopify's money filter / lc-quick-add.js */
  const formatMoney = (cents) => {
    const format = window.lcMoneyFormat || '${{amount}}';
    const match = format.match(/\{\{\s*(\w+)\s*\}\}/);
    const key = match ? match[1] : 'amount';
    const withDelimiters = (precision, thousands = ',', decimal = '.') => {
      const [whole, fraction] = (cents / 100).toFixed(precision).split('.');
      return whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands) + (fraction ? decimal + fraction : '');
    };
    const value = {
      amount: () => withDelimiters(2),
      amount_no_decimals: () => withDelimiters(0),
      amount_with_comma_separator: () => withDelimiters(2, '.', ','),
      amount_no_decimals_with_comma_separator: () => withDelimiters(0, '.', ','),
    }[key];
    return format.replace(/\{\{\s*\w+\s*\}\}/, value ? value() : withDelimiters(2));
  };

  const progressEl = () => drawer.querySelector('.lc-rt');

  const restOf = (rt) => {
    const rest = rt.querySelector('.lc-rt__rest');
    return rest ? parseFloat(rest.style.width) : NaN;
  };

  /** Moves the grey cover to `target`, animating from `from` when known. */
  const setRest = (rt, target, from) => {
    const rest = rt.querySelector('.lc-rt__rest');
    if (!rest || Number.isNaN(target)) return;
    const to = clamp(round1(target));
    if (reduceMotion || from == null || Number.isNaN(from) || from === to) {
      rest.style.transition = 'none';
      rest.style.width = `${to}%`;
    } else {
      rest.style.transition = 'none';
      rest.style.width = `${clamp(from)}%`;
      void rest.offsetWidth; // commit the start width before animating
      rest.style.transition = BAR_EASE;
      rest.style.width = `${to}%`;
    }
    shownRest = to;
  };

  const setDone = (stop, done) => {
    const dot = stop.querySelector('.lc-rt__dot');
    if (dot && !reduceMotion) dot.style.transition = DOT_EASE;
    stop.classList.toggle('is-done', done);
    stop.querySelectorAll('[data-lc-ico]').forEach((icon) => {
      icon.hidden = (icon.dataset.lcIco === 'done') !== done;
    });
  };

  /** Same maths as cart-drawer-tracker.liquid, so the server render matches. */
  const compute = (rt, total) => {
    const stops = Array.from(rt.querySelectorAll('.lc-rt__stop')).map((el) => ({
      el,
      amount: Number(el.dataset.amount) || 0,
      pos: Number(el.dataset.pos) || 0,
      label: el.dataset.label || '',
    }));
    let fill = 100;
    let prevAmount = 0;
    let prevPos = 0;
    let next = null;
    stops.forEach((stop) => {
      if (!next && total < stop.amount) {
        next = stop;
        const segAmount = stop.amount - prevAmount;
        const segPos = stop.pos - prevPos;
        fill = segAmount <= 0 ? stop.pos : ((total - prevAmount) * segPos) / segAmount + prevPos;
      }
      prevAmount = stop.amount;
      prevPos = stop.pos;
    });
    return { stops, fill: clamp(fill), next };
  };

  /** Paints the bar for a cart total (cents) without waiting for Shopify. */
  const apply = (total) => {
    const rt = progressEl();
    if (!rt || total == null) return;
    const { stops, fill, next } = compute(rt, total);
    const from = shownRest != null ? shownRest : restOf(rt);
    setRest(rt, 100 - fill, from);
    stops.forEach((stop) => setDone(stop.el, total >= stop.amount));

    const message = next
      ? (rt.dataset.msg || '')
          .split('[amount]').join(formatMoney(next.amount - total))
          .split('[label]').join(next.label)
      : rt.dataset.msgDone || '';
    const msgEl = rt.querySelector('.lc-rt__msg');
    if (msgEl && message) msgEl.textContent = message;
    const track = rt.querySelector('.lc-rt__track');
    if (track) {
      track.setAttribute('aria-valuenow', String(round1(fill)));
      if (message) track.setAttribute('aria-label', message);
    }
  };

  /** Qualifying total from the lines on screen; overrides = {lineIndex: qty}. */
  const predictedTotal = (overrides = {}) => {
    const lines = drawer.querySelectorAll('.lc-line[data-lc-unit]');
    if (!lines.length) return null;
    let total = 0;
    lines.forEach((line) => {
      const index = line.dataset.lcLine;
      const input = line.querySelector('.quantity__input');
      let qty = parseInt(input ? input.value : line.dataset.lcQty, 10);
      if (pendingRemovals.has(index)) qty = 0;
      if (index in overrides) qty = overrides[index];
      total += (Number(line.dataset.lcUnit) || 0) * Math.max(0, qty || 0);
    });
    return total;
  };

  /* --- Instant updates from customer actions (capture phase, before Dawn) --- */
  document.addEventListener(
    'change',
    (event) => {
      if (!event.target.matches || !event.target.matches('cart-drawer .quantity__input')) return;
      apply(predictedTotal());
    },
    true
  );

  document.addEventListener(
    'click',
    (event) => {
      const remover = event.target.closest && event.target.closest('cart-drawer cart-remove-button');
      const line = remover && remover.closest('.lc-line');
      if (!line) return;
      pendingRemovals.add(line.dataset.lcLine);
      apply(predictedTotal());
    },
    true
  );

  /* --- Smooth transition whenever the drawer is re-rendered --- */
  const revealPreviewNotes = () => {
    if (isLiveTheme) return;
    drawer.querySelectorAll('[data-lc-preview-only][hidden]').forEach((el) => {
      el.hidden = false;
    });
  };

  const onRender = () => {
    revealPreviewNotes();
    // A fresh item list means Shopify's answer arrived: on-screen removals are settled.
    const list = drawer.querySelector('.lc-cd__items');
    if (list && !list.__lcSeen) {
      list.__lcSeen = true;
      pendingRemovals.clear();
    }
    const rt = progressEl();
    if (!rt) {
      shownRest = null;
      return;
    }
    if (rt.__lcPainted) return;
    rt.__lcPainted = true;
    setRest(rt, restOf(rt), shownRest);
  };

  new MutationObserver(onRender).observe(drawer, { childList: true, subtree: true });
  onRender();

  window.LcCartProgress = { apply, predictedTotal, formatMoney };
})();

/**
 * LaneCult cart: one row per product
 * ------------------------------------------------------------------------
 * When Shopify splits one product over several cart lines (automatic discounts),
 * the drawer and /cart show a single row carrying data-lc-group-keys (see
 * snippets/lc-cart-drawer-item.liquid). Dawn would change only one of those
 * lines, so + / − / typed quantities and Remove on such a row are handled here:
 * one cart/update.js request sets the first line to the new total and the
 * others to 0. The cart queue in cart-drawer.liquid then refreshes the drawer
 * and the cart page as for any other cart change.
 */
(() => {
  if (window.LcCartGroups) return;

  const ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const groupRow = (el) => (el && el.closest ? el.closest('.lc-line[data-lc-group-keys]') : null);

  const setTotal = (row, total) => {
    const keys = (row.dataset.lcGroupKeys || '').split(',').filter(Boolean);
    if (!keys.length) return Promise.resolve();
    const updates = {};
    keys.forEach((key, index) => {
      updates[key] = index === 0 ? total : 0;
    });
    row.setAttribute('aria-busy', 'true');
    row.style.opacity = '0.6';
    const spinner = row.querySelector('.loading__spinner');
    if (spinner) spinner.classList.remove('hidden');
    return fetch(`${ROOT}cart/update.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ updates }),
    })
      .catch((error) => console.warn('[LcCartGroups]', error))
      .finally(() => {
        // The refreshed cart replaces the row; restore it if it is still here.
        row.removeAttribute('aria-busy');
        row.style.opacity = '';
        if (spinner) spinner.classList.add('hidden');
      });
  };

  // Capture phase: runs before Dawn's cart-items / quantity-input listeners.
  document.addEventListener(
    'change',
    (event) => {
      const input = event.target;
      if (!input || !input.matches || !input.matches('.lc-qty__input')) return;
      const row = groupRow(input);
      if (!row) return;
      event.stopImmediatePropagation();
      const total = Math.max(0, parseInt(input.value, 10) || 0);
      if (String(total) === row.dataset.lcQty) return;
      setTotal(row, total);
    },
    true
  );

  document.addEventListener(
    'click',
    (event) => {
      const remover = event.target && event.target.closest ? event.target.closest('cart-remove-button') : null;
      const row = groupRow(remover);
      if (!row) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      setTotal(row, 0);
    },
    true
  );

  window.LcCartGroups = { setTotal };
})();
